import { inject, Injectable } from '@angular/core';
import {
  AuthenticatedResult,
  EventTypes,
  LoginResponse,
  OidcSecurityService,
  PublicEventsService,
} from 'angular-auth-oidc-client';
import { filter, Observable, map, of, catchError, tap, switchMap, throwError } from 'rxjs';

import { OidcUserData } from '@auth/interfaces/oidc-user-data.interface';
import { WINDOW } from '@core/consts/browser-providers.const';
import { environment } from '@env/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly publicEventService = inject(PublicEventsService);
  private readonly window = inject(WINDOW);

  private loginResponse: LoginResponse | null = null;
  private _userId: string | null = null;
  private _accessToken: string | null = null;

  get userId(): string | null {
    return this._userId;
  }

  get accessToken(): string | null {
    return this._accessToken;
  }

  get isAuthenticated$(): Observable<AuthenticatedResult> {
    return this.oidcSecurityService.isAuthenticated$;
  }

  login(): void {
    this.oidcSecurityService.authorize();
  }

  logout(): Observable<unknown> {
    return this.oidcSecurityService.logoff().pipe(
      tap(() => sessionStorage.removeItem('user-organization')),
      catchError((error) => {
        console.error('Error logging out:', error);
        this.window.location.reload();
        return throwError(() => error);
      }),
    );
  }

  checkAuth(): Observable<LoginResponse> {
    let request = of(this.loginResponse as LoginResponse);
    if (this.loginResponse == null) {
      request = this.oidcSecurityService.checkAuth();
    }

    return request.pipe(
      map((loginResponse) => {
        const oidcUserData = loginResponse.userData as OidcUserData;
        this._userId = oidcUserData.sub;
        this._accessToken = loginResponse.accessToken;
        return { ...loginResponse, userData: oidcUserData };
      }),
      tap(({ isAuthenticated }) => this.alertUserAuthenticated(isAuthenticated)),
      tap((loginResponse) => loginResponse.isAuthenticated && (this.loginResponse = loginResponse)),
    );
  }

  refreshToken(): Observable<string> {
    return this.publicEventService.registerForEvents().pipe(
      filter((notification) => notification.type === EventTypes.NewAuthenticationResult),
      switchMap(() => this.oidcSecurityService.getAccessToken()),
      tap((token) => (this._accessToken = token)),
    );
  }

  private alertUserAuthenticated(isAuthenticated: boolean): void {
    if (environment.env === 'prod' || this.loginResponse) {
      return;
    }
    const getAlertFormat = (color: 'green' | 'red'): string =>
      `background-color: ${color}; color: white; font-weight: bold; padding: 8px;`;
    let message = '%cIS NOT AUTH';
    let format = getAlertFormat('red');
    if (isAuthenticated) {
      message = '%cIS AUTH';
      format = getAlertFormat('green');
    }
    console.info(message, format);
  }
}
